import React, { useCallback, useLayoutEffect } from 'react';
import { useLocalSearchParams, useNavigation } from 'expo-router';
import { defineMessages, useIntl } from 'react-intl';

import { IconLink, ModalCloseBtn, useResponsive } from '@sg/garnish';

import { useMomoSurveyUrl } from '@order/features-order-status';

import { SignedOutView, TransparentModal } from '../../components';
import { useIsLoggedIn } from '../../context/AuthMachine';
import { useCustomer } from '../../context/Customer';
import { useDerivedRateOrderStatus, useMomosOrder } from './hooks';
import { useRateOrderNavigation } from './RateOrder.navigation';
import { RateOrderScreenReady } from './RateOrderScreen.ready';

// ─────────────────────────────────────────────────────────────────────────────

export const RateOrderScreen = () => {
  const isLoggedIn = useIsLoggedIn();
  const { customer } = useCustomer();
  const { minWidth, match } = useResponsive();
  const { formatMessage } = useIntl();

  // ─── Navigation Helpers ──────────────────────────────────────────────

  const { orderId, isInStoreOrder: isInStoreOrderParam } = useLocalSearchParams<SearchParams>();
  const { dismissRateOrder } = useRateOrderNavigation();
  const navigation = useNavigation();
  const setOptions = navigation.setOptions;

  const renderNavigationHeaderRight = useCallback(
    () => (
      <IconLink
        width={36}
        height={36}
        name="IconClose"
        palette="muted"
        accessibilityLabel={formatMessage(messages.rateOrderClose)}
        onPress={dismissRateOrder}
      />
    ),
    [dismissRateOrder, formatMessage],
  );

  useLayoutEffect(() => {
    setOptions({ headerRight: renderNavigationHeaderRight });
  }, [renderNavigationHeaderRight, setOptions]);

  // ─── Derived Order Data ──────────────────────────────────────────────

  const orderTypeIsInStore = isInStoreOrderParam === 'true';

  const { isFetching, isInvalidOrder, isPendingOrder, isRatedOrder, order } =
    useDerivedRateOrderStatus(orderId, orderTypeIsInStore);

  const orderType = order?.orderType;

  const momosOrder = useMomosOrder({ order });
  const momoSurveyUrl = useMomoSurveyUrl(customer, momosOrder);
  const showMomosSurvey = !!momoSurveyUrl && !isFetching && !isInvalidOrder && !isPendingOrder;

  // ─────────────────────────────────────────────────────────────────────

  return (
    <TransparentModal
      size={match(['full', showMomosSurvey ? 'large' : 'medium'])}
      dialogMaxHeight={showMomosSurvey ? MOMOS_MODAL_MAX_HEIGHT : undefined}
      dialogMaxWidth={showMomosSurvey ? MOMOS_MODAL_MAX_WIDTH : undefined}
      accessibilityLabel={showMomosSurvey ? formatMessage(messages.momosSurveyTitle) : undefined}
      onClose={dismissRateOrder}
    >
      {minWidth.isSM ? <ModalCloseBtn onPress={dismissRateOrder} /> : null}

      {isLoggedIn ? (
        <RateOrderScreenReady
          isFetching={isFetching}
          isInvalidOrder={isInvalidOrder}
          isPendingOrder={isPendingOrder}
          isRatedOrder={isRatedOrder}
          momoSurveyUrl={momoSurveyUrl}
          orderId={orderId}
          orderType={orderType ?? null}
        />
      ) : (
        <SignedOutView redirectTo="/account/orders" withoutFooter />
      )}
    </TransparentModal>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const MOMOS_MODAL_MAX_HEIGHT = 720;
const MOMOS_MODAL_MAX_WIDTH = 600;

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  rateOrderClose: {
    defaultMessage: 'Close',
    description: 'Rate Order | Close',
  },
  rateOrderNotFoundOrderViewTitle: {
    defaultMessage: `We couldn't find this order.`,
    description: 'Rate | Order | Not Found Order View | Title',
  },
  rateOrderNotFoundOrderViewSubtitle: {
    defaultMessage: 'Please select another order to rate.',
    description: 'Rate | Order | Not Found Order View | Subtitle',
  },
  rateOrderPendingOrderViewTitle: {
    defaultMessage: 'This order is still in progress.',
    description: 'Rate | Order | Pending Order View | Title',
  },
  rateOrderPendingOrderViewSubtitle: {
    defaultMessage: 'Come back to rate this order once it has been completed.',
    description: 'Rate | Order | Pending Order View | Subtitle',
  },
  rateOrderRatedOrderViewTitle: {
    defaultMessage: 'You’ve already rated this order.',
    description: 'Rate | Order | Rated Order View | Title',
  },
  rateOrderRatedOrderViewSubtitle: {
    defaultMessage: 'Thank you! Your feedback means a lot.',
    description: 'Rate | Order | Rated Order View | Subtitle',
  },
  momosSurveyTitle: {
    defaultMessage: 'Rate order survey',
    description: 'Rate | Order | Momos Survey | Modal Accessibility Label',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type SearchParams = {
  orderId: string;
  isInStoreOrder?: 'true' | 'false';
};
